import { TextRun } from 'docx';
import { NORMALIZED_TITLE_KEYWORDS } from '../constants/resumeKeywords';
import { FONT, BODY_SIZE } from '../constants/docxStyles';

const FILE_LABELS = {
  "English (US)": {
    resume: "Resume",
    cv: "CV",
    coverLetter: "Cover_Letter",
    linkedin: "LinkedIn_Profile",
    bio: "Bio",
    portfolio: "Portfolio"
  },
  Spanish: {
    resume: "Curriculum",
    cv: "CV",
    coverLetter: "Carta_de_Presentacion",
    linkedin: "Perfil_LinkedIn",
    bio: "Biografia",
    portfolio: "Portafolio"
  },
  German: {
    resume: "Lebenslauf",
    cv: "Lebenslauf",
    coverLetter: "Anschreiben",
    linkedin: "LinkedIn_Profil",
    bio: "Biografie",
    portfolio: "Portfolio"
  },
  French: {
    resume: "CV",
    cv: "CV",
    coverLetter: "Lettre_de_Motivation",
    linkedin: "Profil_LinkedIn",
    bio: "Biographie",
    portfolio: "Portfolio"
  }
};

export const isTitleLine = (line) => {
  if (!line) return false;

  const normalized = line.trim().toLowerCase();
  return NORMALIZED_TITLE_KEYWORDS.has(normalized);
};

// Matches any of:
//   LinkedIn: https://linkedin.com/in/...
//   LinkedIn: linkedin.com/in/...
//   LinkedIn: www.linkedin.com/in/...
//   https://linkedin.com/in/...  (bare URL, any position)
export const isLinkedInLine = (line) =>
  /^LinkedIn:\s*(?:https?:\/\/)?(?:www\.)?linkedin\.com\//i.test(line.trim()) ||
  /(?:^|\s)(?:https?:\/\/)?(?:www\.)?linkedin\.com\/in\//i.test(line.trim());

export const extractLinkedInUrl = (line) => {
  const trimmed = line.trim();
  // First try "LinkedIn: URL" format
  const labelMatch = trimmed.match(/^LinkedIn:\s*((?:https?:\/\/)?(?:www\.)?linkedin\.com\/\S+)/i);
  if (labelMatch) {
    const url = labelMatch[1];
    return url.startsWith('http') ? url : `https://${url}`;
  }
  // Try bare URL format (with or without protocol) — /in/, /pub/, /company/, etc.
  const bareMatch = trimmed.match(/(?:^|\s)((?:https?:\/\/)?(?:www\.)?linkedin\.com\/\S+)/i);
  if (bareMatch) {
    const url = bareMatch[1];
    return url.startsWith('http') ? url : `https://${url}`;
  }
  return null;
};

export const isResumeHeader = (line) => {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (/^[•\-–—]/.test(trimmed)) return false;

  // Experience heading: "Company – City, State" or "Job Title – Month YYYY – Present"
  // Must have an en dash or ASCII hyphen AND a date or location after it; checked before the length limit
  if (
    /\s[-–]\s/.test(trimmed) &&
    /[-–]\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|Present|\d{4}|[A-Z][a-z]+,)/i.test(trimmed)
  ) return true;

  if (/[.!?]$/.test(trimmed)) return false;
  // ALL-CAPS section header (must contain at least one letter)
  if (trimmed === trimmed.toUpperCase() && /[A-Z]/.test(trimmed) && trimmed.length <= 60) return true;
  // Known Title Case section header — checked before length guard so "Professional Summary" always matches
  if (NORMALIZED_TITLE_KEYWORDS.has(trimmed.toLowerCase())) return true;
  // Colon-terminated sub-header (e.g. "Education:" or "Technical Skills:")
  if (/^[A-Z][A-Za-z\s&/]+:$/.test(trimmed)) return true;
  // Length guard for everything else — prevents body sentences from matching
  if (trimmed.length > 60) return false;
  return false;
};

// Parses a single line for inline **bold** markers.
// All header/section classification is handled upstream in the Layout line loop —
// this function only strips ** markers and returns TextRun[].
export const parseTextWithBold = (line) => {
  const runs = [];
  const regex = /\*\*(.*?)\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = regex.exec(line)) !== null) {
    if (match.index > lastIndex) {
      runs.push(
        new TextRun({
          text: line.substring(lastIndex, match.index),
          font: FONT,
          size: BODY_SIZE
        })
      );
    }
    // Strip markers; render content as plain text
    runs.push(
      new TextRun({
        text: match[1],
        font: FONT,
        size: BODY_SIZE
      })
    );
    lastIndex = regex.lastIndex;
  }

  if (lastIndex < line.length) {
    runs.push(
      new TextRun({
        text: line.substring(lastIndex),
        font: FONT,
        size: BODY_SIZE
      })
    );
  }

  return runs;
};

const normalizeTechnicalSkillsTitleCase = (text) => {
  const lines = text.split('\n');
  let inTechSkills = false;

  return lines.map((line) => {
    const trimmed = line.trim();

    if (/^technical\s+skills?[:\s]*$/i.test(trimmed)) {
      inTechSkills = true;
      return line;
    }

    // Exit section when a new section header is encountered
    if (inTechSkills && trimmed.length > 0 && NORMALIZED_TITLE_KEYWORDS.has(trimmed.toLowerCase())) {
      inTechSkills = false;
    }

    if (!inTechSkills || !trimmed) return line;

    // Strip leading bullet if present, apply Title Case to each comma-separated skill
    const bullet = /^[•-]\s*/.exec(trimmed)?.[0] ?? '';
    const skillContent = trimmed.slice(bullet.length);

    const titleCased = skillContent.split(',').map((skill) =>
      skill.trim().replace(/\b\w+/g, (word) =>
        // Preserve ALL-CAPS acronyms (KPI, UAT, UX, AI, SOP, etc.)
        /^[A-Z]{2,}$/.test(word) ? word : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
    ).join(' • ');

    return bullet + titleCased;
  }).join('\n');
};

export const cleanNumbersForDownload = (text, docType = "") => {
  if (typeof text !== "string") return "";

  let cleaned = text;

  // removed: now enforced at prompt level (RESUME_PROMPT output instruction)
  // cleaned = cleaned.replace(/^\s*\[\s*ALIGNMENT\s*:[^\]]*\]\s*$/gim, "");

  // removed: now enforced at prompt level (RESUME_PROMPT output instruction)
  // cleaned = cleaned.replace(/^\s*\[\s*DRAFT\s*MODE[^\]]*\]\s*$/gim, "");

  // removed: now enforced at prompt level (RESUME_PROMPT output instruction)
  // cleaned = cleaned.replace(/JD\s*keyword[s]?\s*hits?\s*\(?.*?\)?:[\s\S]*?(?=\n\s*\n|\n[A-Z][A-Z\s&]{3,})/gi, "");

  // -----------------------------
  // 1. Remove AI markers — [Metric], [JD Match], [ALIGNMENT: ...], [DRAFT MODE ...]
  // -----------------------------
  cleaned = cleaned
    .replace(/\[\s*(💡|✓)?\s*metric\s*\]/gi, "")
    .replace(/\[\s*(✓)?\s*jd\s*match\s*\]/gi, "")
    .replace(/\bJD\s*Match\b/gi, "")
    .replace(/^\s*\[\s*ALIGNMENT\s*:[^\]]*\]\s*$/gim, "")
    .replace(/\[\s*ALIGNMENT\s*:[^\]]*\]/gi, "")
    .replace(/^\s*\[\s*DRAFT\s*MODE[^\]]*\]\s*$/gim, "");

  // Strip [DRAFT MODE NOTES] section header and all indented bullets beneath it
  cleaned = cleaned.replace(
    /\[\s*DRAFT\s*MODE\s*NOTES?\s*\][\s\S]*?(?=\n\s*\n[A-Z]|\n[A-Z]{2,}|$)/gi,
    ""
  );


  // -----------------------------
  // 1b. Strip "Application:" metadata block
  //     The AI sometimes outputs a block with application-specific metadata
  //     (company, hiring manager, date applied) under an "Application:" header.
  //     Strip it entirely — this data belongs in the form, not the resume.
  // -----------------------------
  cleaned = cleaned.replace(
    /^Application:[\s\S]*?(?=\n\s*\n[A-Z]|\n[A-Z]{2,}|$)/gim,
    ""
  );
  // Also strip standalone metadata fields that may appear anywhere
  cleaned = cleaned.replace(/^Hiring Manager:.*$/gim, "");
  cleaned = cleaned.replace(/^Date Applied:.*$/gim, "");
  cleaned = cleaned.replace(/^Application Date:.*$/gim, "");


  // -----------------------------
  // 2. REMOVE everything after scoring block (2nd page)
  // -----------------------------
  cleaned = cleaned.replace(
    /\{\s*"ATS"[\s\S]*$/gi,
    ""
  );

  cleaned = cleaned.replace(
    /---\s*Zynacular\s*DRAFT\s*MODE[\s\S]*$/gi,
    ""
  );


  // -----------------------------
  // 3. Strip keyword frequency counts — simple (12) and compound (Leadership: 4, Pipeline: 6)
  //
  //    Pass 1: letter (immediately or with space) before count — covers the common case.
  //    Pass 2: any whitespace before count — catches "5+ (2)", "• (3)", etc. where a
  //            non-letter char precedes the space.
  //    Both passes use (?!\s*\d) to protect phone numbers: "(415) 555-…" has a digit
  //    immediately after the closing ), so the negative lookahead blocks the strip.
  // -----------------------------
  cleaned = cleaned.replace(/(?<=[A-Za-z])\s*\(\d+\)(?!\s*\d)/g, "");  // Pass 1
  cleaned = cleaned.replace(/\s\(\d+\)(?!\s*\d)/g, "");                 // Pass 2
  cleaned = cleaned.replace(
    /\(\s*[A-Za-z][A-Za-z\s]*:\s*\d+(?:\s*,\s*[A-Za-z][A-Za-z\s]*:\s*\d+)*\s*\)/g,
    ""
  );


  // -----------------------------
  // 3c. Strip scope line parenthetical placeholders
  //     Matches: (budget not provided), (amount not provided), (X not provided), etc.
  // -----------------------------
  cleaned = cleaned.replace(/\s*\([^)]*not provided[^)]*\)/gi, "");


  // -----------------------------
  // 3b + 6a + 6b + 6c + 6d — Name artifact stripping, scoped to Professional Summary body only.
  //
  //  Three-part split: preHead | summaryBody | afterSummary
  //    preHead     = everything up to and including the summary heading line
  //                  (document header: name, city, phone, email — NEVER touched)
  //    summaryBody = text between summary heading and next section heading — only part processed
  //    afterSummary = Experience, Education, Skills, etc. — never touched
  //
  //  Previous two-part split (preSummary/postSummary) still included the document header in
  //  preSummary, which is why "San Francisco, CA" kept getting stripped to "CA".
  // -----------------------------
  {
    const summaryHeadMatch = /^[ \t]*(?:PROFESSIONAL\s+)?SUMMARY[^\n]*/im.exec(cleaned);

    if (summaryHeadMatch) {
      const headEnd = summaryHeadMatch.index + summaryHeadMatch[0].length;
      const afterHead = cleaned.slice(headEnd);
      const lines = afterHead.split('\n');

      // Walk lines to find where the next section heading begins.
      // A section heading is ALL-CAPS or a NORMALIZED_TITLE_KEYWORDS entry preceded by a blank line.
      let charPos = 0;
      let splitIdx = afterHead.length; // fallback: treat remainder as summary body

      for (let i = 0; i < lines.length; i++) {
        const t = lines[i].trim();
        if (
          t.length >= 3 &&
          i > 0 &&
          lines[i - 1].trim() === '' &&
          (
            (t === t.toUpperCase() && /[A-Z]/.test(t) && t.length <= 60) ||
            NORMALIZED_TITLE_KEYWORDS.has(t.toLowerCase())
          )
        ) {
          splitIdx = charPos;
          break;
        }
        charPos += lines[i].length + 1; // +1 for the \n that split() consumed
      }

      const preHead      = cleaned.slice(0, headEnd);
      let   summaryBody  = cleaned.slice(headEnd, headEnd + splitIdx);
      const afterSummary = cleaned.slice(headEnd + splitIdx);

      // 3b — "Firstname Lastname — **Bold Title**" (bold markers still present at this point)
      summaryBody = summaryBody.replace(
        /^([A-Z][a-zA-Z'-]+ [A-Z][a-zA-Z'-]+)\s*[-–—]\s*(?![A-Z]{2}\b)\*\*[^*\n]+\*\*/gm,
        ""
      );

      // 6a — dash or pipe variant: "Firstname Lastname –/—/-/| …"
      summaryBody = summaryBody.replace(
        /^([A-Z][a-zA-Z'-]+ [A-Z][a-zA-Z'-]+)\s*[-–—|]\s*(?!(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+\d{4}|[A-Z][a-z]+,\s*[A-Z]|[A-Z]{2}\b)/gm,
        ""
      );

      // 6b pass 1 — standalone artifact line ending with comma
      summaryBody = summaryBody.replace(
        /^[A-Z][a-zA-Z'-]+ [A-Z][a-zA-Z'-]+,\s*(?![A-Z]{2}\s*,?\s*$)[A-Za-z][^.!?\n]*?,$/gm,
        ""
      );

      // 6b pass 2 — inline "Firstname Lastname, " prefix
      summaryBody = summaryBody.replace(
        /^([A-Z][a-zA-Z'-]+ [A-Z][a-zA-Z'-]+),\s*(?![A-Z][a-z]+,\s*[A-Z]|[A-Z]{2}\b|(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b)/gm,
        ""
      );

      // 6c — orphaned leading punctuation after name-strip
      summaryBody = summaryBody.replace(/^[–—|]\s+/gm, "");   // orphaned em/en dash or pipe
      summaryBody = summaryBody.replace(/^,\s+/gm, "");         // orphaned comma
      summaryBody = summaryBody.replace(/^-(?=[a-z])/gm, "");  // orphaned hyphen before lowercase
      summaryBody = summaryBody.replace(/\s*\bcandidate,?\s*/gi, ' ').replace(/ {2,}/g, ' ');

      // 6d — standalone pipe variant: "Firstname Lastname | …" (no lookahead needed —
      //      a pipe separator is never used in geographic or date patterns)
      summaryBody = summaryBody.replace(
        /^([A-Z][a-zA-Z'-]+ [A-Z][a-zA-Z'-]+)\s*\|\s*/gm,
        ""
      );

      // 6e — strip sentence-starting first-person pronouns (scoped to summaryBody only)
      // Handles: "I lead" → "Lead", "I've built" → "Built", "I'm passionate" → "Passionate"
      summaryBody = summaryBody.replace(
        /(^|[.!?]\s+)I(?:['\u2019](?:ve|m))?\s+([a-zA-Z])/gm,
        (_, prefix, nextChar) => prefix + nextChar.toUpperCase()
      );
      summaryBody = summaryBody.replace(/ {2,}/g, ' ');

      cleaned = preHead + summaryBody + afterSummary;
    }
    // If no summary heading is found, skip name-strip passes entirely.
    // The artifact only appears in a Professional Summary section.
  }


  // -----------------------------
  // 4. Strip all **bold** markers (keep content, remove markers)
  //    Pass 1: paired markers — handles **content** on same line
  //    Pass 2: any remaining orphaned ** (unpaired, or cross-line) that
  //            Pass 1 missed because `.` does not match \n
  // -----------------------------
  cleaned = cleaned.replace(/\*\*(.*?)\*\*/g, "$1");
  cleaned = cleaned.replace(/\*\*/g, "");


  // -----------------------------
  // 4b. Portfolio: strip "SECTION N — " prefix from section header lines
  // -----------------------------
  if (docType === 'portfolio') {
    cleaned = cleaned.replace(/^SECTION\s+\d+\s*[—–-]+\s*/gim, '');
  }


  // -----------------------------
  // 4c. LinkedIn: strip leading "N. " from ALL-CAPS section header lines
  //     Anchored with $ so numbered body text (lists, activity plan) is safe
  // -----------------------------
  if (docType === 'linkedin') {
    cleaned = cleaned.replace(/^\d+\.\s+(?=[A-Z0-9][A-Z0-9\s/—()-]+$)/gm, '');
  }


  // -----------------------------
  // 4d. Narrative doc types: strip instruction echoes and clean sub-labels
  //
  //     The AI sometimes echoes structural instructions from the prompt back
  //     into the output — tier prefixes, parenthetical word counts, sub-labels
  //     like "Structure:", "Opening:", "Pillar 1: Core Skill → ...". These
  //     passes remove the noise while leaving actual content untouched.
  // -----------------------------
  if (docType === 'bio') {
    // "TIER 1 — TAGLINE (≤25 words)" → "TAGLINE"
    // "TIER 2 — SHORT BIO (60–75 words)" → "SHORT BIO"
    cleaned = cleaned.replace(/^TIER\s+\d+\s*[—–-]+\s*/gim, '');
    // Strip parenthetical word count from tier name lines
    cleaned = cleaned.replace(/^([A-Z][A-Z\s/]+)\s+\([\d≤–\-\s]+words?\)\s*$/gm, '$1');
    // Strip Narrative Bio structural prefixes: "A. Identity Frame" → "Identity Frame"
    cleaned = cleaned.replace(/^[A-E]\.\s+/gm, '');
    // Strip sub-label-only lines with no body content
    cleaned = cleaned.replace(/^(?:Opening|Middle|Close)\s*:\s*$/gim, '');
  }

  if (docType === 'linkedin') {
    // Strip parenthetical character/word count annotations from heading-style lines
    // "HEADLINE (≤220 CHARACTERS)" → "HEADLINE"
    // "HEADLINE VARIATIONS (3 ALTERNATIVES)" → "HEADLINE VARIATIONS"
    cleaned = cleaned.replace(/^([A-Z0-9][A-Z0-9\s/]+)\s+\([^)]+\)\s*$/gm, '$1');
    // Strip standalone "Structure:" instruction lines
    cleaned = cleaned.replace(/^Structure:\s*$/gim, '');
    // Strip instruction sub-label bullets echoed from prompt structure
    cleaned = cleaned.replace(
      /^[-•]\s+(?:Hook line|Value proposition|Achievement block|Specialization statement|Call to action)\s*:.+$/gim,
      ''
    );
    // Strip tone / format / group instruction lines
    cleaned = cleaned.replace(/^(?:Tone|Format|Group)\s*:.+$/gim, '');
    cleaned = cleaned.replace(/^Ranked by\b.+$/gim, '');
    cleaned = cleaned.replace(/^(?:Omit section entirely|Include only if present in resume)\b.+$/gim, '');
  }

  if (docType === 'portfolio') {
    // Convert instructional sub-labels to clean headings
    cleaned = cleaned.replace(/^Headline\s*\([^)]+\)\s*:/gim, 'HEADLINE');
    cleaned = cleaned.replace(/^About\s+Statement\s*(?:\([^)]+\))?\s*:/gim, 'ABOUT');
    cleaned = cleaned.replace(/^Three-Pillar\s+Value\s+Statement\s*(?:\([^)]+\))?\s*:/gim, 'VALUE PILLARS');
    // Strip parenthetical annotations from remaining ALL-CAPS headings
    // "CASE STUDY ENGINE (3–5 PROJECTS)" → "CASE STUDY ENGINE"
    cleaned = cleaned.replace(/^([A-Z][A-Z\s]+)\s+\([^)]+\)\s*$/gm, '$1');
    // Strip "Pillar N:" template placeholder lines
    cleaned = cleaned.replace(/^Pillar\s+\d+:\s*(?:Core|Secondary|Leadership)\s+Skill\s*→.*$/gim, '');
    // Strip instruction-only labels in case studies (keep Challenge/Action/Result/Insight)
    cleaned = cleaned.replace(/^Title\s*:\s*(?:\[.*?\])?\s*$/gim, '');
    cleaned = cleaned.replace(/^JD Alignment\s*:\s*(?:\[.*?\])?\s*$/gim, '');
    // Strip "Integrity statement (verbatim):" prefix → the actual statement follows inline
    cleaned = cleaned.replace(/^Integrity\s+(?:statement\s*)?(?:\(verbatim\))?\s*:\s*/gim, '');
    // Strip "Contact block:", "Availability line:" prefixes
    cleaned = cleaned.replace(/^(?:Contact block|Availability line)\s*:\s*/gim, '');
    // Strip instruction-only lines about what to include/use
    cleaned = cleaned.replace(
      /^(?:Include only data|Use only projects|Write [""]Not applicable[""]|All pillars must|Close with:)\b.*$/gim,
      ''
    );
  }


  // -----------------------------
  // 5. Strip backtick characters
  // -----------------------------
  cleaned = cleaned.replace(/`/g, "");


  // -----------------------------
  // 7. Fix A•B Testing artifacts — only the literal A•B pattern should be /
  // -----------------------------
  cleaned = cleaned.replace(/\bA\s*•\s*B\b/g, "A/B");


  // -----------------------------
  // 8. Normalize Technical Skills section to Title Case
  // -----------------------------
  cleaned = normalizeTechnicalSkillsTitleCase(cleaned);


  // -----------------------------
  // 8b. Strip Draft Mode metadata (Keyword Frequency block + Validation lines)
  // -----------------------------
  cleaned = cleaned.replace(/\n*Keyword Frequency[\s\S]*$/i, "");
  cleaned = cleaned.replace(/^Validation:.*$/gim, "");


  // -----------------------------
  // 8c. Strip hallucinated Additional Information content
  // -----------------------------
  cleaned = cleaned.replace(/^Available:.*$/gim, "");
  cleaned = cleaned.replace(/^clearly state your role.*$/gim, "");
  // Skip for cover letters — body text can legitimately open with these words
  // (e.g. "Add to that my experience…", "Include me in your consideration…")
  if (docType !== 'coverLetter') {
    cleaned = cleaned.replace(
      /^(?:mention|include|add|state|describe|list|provide)\b.*$/gim,
      ""
    );
  }
  // Drop the section header if nothing real follows (only whitespace before next section)
  cleaned = cleaned.replace(
    /ADDITIONAL INFORMATION\s*\n\s*\n/gi,
    ""
  );


  // -----------------------------
  // 9. Normalize whitespace
  // -----------------------------
  cleaned = cleaned
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return cleaned;
};

export const getLocalizedDocLabel = (docType, language) => {
  const langMap = FILE_LABELS[language] || FILE_LABELS["English (US)"];
  return langMap?.[docType] || docType;
};

// Strip invalid filename characters then replace spaces with underscores
export const sanitizeFilePart = (value) =>
  value
    ?.toString()
    .trim()
    .replace(/[/\\:*?"<>|]/g, "")
    .replace(/\s+/g, '_');

export const extractNameFromContent = (content = "", docType = "") => {
  if (typeof content !== "string" || !content.trim()) return "Candidate";

  const lines = content.split("\n");

  if (docType === "coverLetter") {
    for (const line of lines) {
      const t = line.trim();
      if (!t) continue;
      // Skip date lines (Month DD, YYYY)
      if (/^(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\S*\s+\d{1,2},\s+\d{4}$/i.test(t)) continue;
      // Skip phone, email, city/state, LinkedIn
      if (/\(?\d{3}\)?[\s.-]\d{3}[\s.-]\d{4}/.test(t)) continue;
      if (/@/.test(t)) continue;
      if (/linkedin\.com/i.test(t)) continue;
      if (/[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*,\s+[A-Z]{2}\b/.test(t)) continue;
      return t;
    }
  }

  return lines[0]?.trim() || "Candidate";
};

export const getContentByDocType = (result) => {
  if (!result) return "";

  switch (result.docType) {
    case "resume":
      return result.resumeText || "";
    case "cv":
      return result.resumeText || "";

    case "coverLetter":
      return result.resumeText || "";
    case "linkedin":
      return result.resumeText || "";
    case "bio":
      return result.resumeText || "";
    case "portfolio":
      return result.resumeText || "";

    default:
      // fallback (safety)
      return result.resumeText || result.description || "";
  }
};
